import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { api } from "../lib/api";
import { Search, Gauge, Fuel, Cog } from "lucide-react";

export default function PublicInventoryPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [makeFilter, setMakeFilter] = useState("all");
  const [bodyFilter, setBodyFilter] = useState("all");
  const [priceMax, setPriceMax] = useState([350000]);

  useEffect(() => {
    // Fetch all vehicles (incl. sold) so sold units can show at the bottom
    api.get("/vehicles").then((r) => {
      setVehicles(r.data || []);
      setLoading(false);
    });
  }, []);

  const makes = useMemo(() => Array.from(new Set(vehicles.map((v) => v.make))).sort(), [vehicles]);
  const bodies = useMemo(() => Array.from(new Set(vehicles.map((v) => v.body_type))).sort(), [vehicles]);

  // Sort: available first, pending next, sold last. Within each bucket keep DB order.
  const STATUS_ORDER = { available: 0, pending: 1, auction: 2, wholesale: 3, sold: 4 };
  const filtered = vehicles
    .filter((v) => {
      if (makeFilter !== "all" && v.make !== makeFilter) return false;
      if (bodyFilter !== "all" && v.body_type !== bodyFilter) return false;
      if (v.price > priceMax[0]) return false;
      if (q && !`${v.make} ${v.model} ${v.year} ${v.vin}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

  const availableCount = filtered.filter((v) => v.status === "available").length;
  const soldCount = filtered.filter((v) => v.status === "sold").length;

  return (
    <PublicShell>
      <section className="hero-bg stripe-overlay pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="brand-divider max-w-xs mb-4"><span className="dot" /><span className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>Browse inventory</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-3"><span className="chrome-text">The collection.</span></h1>
          <p className="text-slate-300 max-w-xl">Every vehicle hand-picked. Filter by make, body, price.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-8 relative z-10">
        <div className="glass rounded-md p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <div className="label-eyebrow mb-2">Search</div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Make, model, VIN" className="pl-10 h-11 bg-[#04060d] border-white/10" data-testid="public-inv-search" />
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Make</div>
            <Select value={makeFilter} onValueChange={setMakeFilter}>
              <SelectTrigger className="h-11 bg-[#04060d] border-white/10" data-testid="public-inv-make"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All makes</SelectItem>
                {makes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Body</div>
            <Select value={bodyFilter} onValueChange={setBodyFilter}>
              <SelectTrigger className="h-11 bg-[#04060d] border-white/10" data-testid="public-inv-body"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All bodies</SelectItem>
                {bodies.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="label-eyebrow mb-2">Max price · ${priceMax[0].toLocaleString()}</div>
            <Slider value={priceMax} onValueChange={setPriceMax} min={50000} max={350000} step={5000} data-testid="public-inv-price" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="label-eyebrow mb-6" data-testid="inventory-count">
          <span className="text-white">{availableCount}</span> available
          {soldCount > 0 && <span className="text-slate-500"> · {soldCount} recently sold</span>}
        </div>
        {loading ? <div className="text-slate-400">Loading…</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((v) => {
              const isSold = v.status === "sold";
              const CardTag = isSold ? "div" : Link;
              const cardProps = isSold ? {} : { to: `/inventory/${v.id}` };
              return (
                <CardTag
                  key={v.id}
                  {...cardProps}
                  className={`surface rounded-md overflow-hidden block group relative ${isSold ? "opacity-70 cursor-not-allowed" : "hover-lift"}`}
                  data-testid={`pub-vehicle-${v.id}`}
                  data-status={v.status}
                >
                  <div className="absolute top-0 left-0 right-0 h-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(90deg,transparent,#e63946 25%,#d4af37 75%,transparent)" }} />
                  <div
                    className="aspect-[16/10] bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${v.images?.[0]})` }}
                    role="img"
                    aria-label={`${v.year} ${v.make} ${v.model}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0b1324] via-transparent to-transparent opacity-80" />
                    {isSold && (
                      <div
                        className="absolute top-3 left-3 px-3 py-1 rounded-sm font-heading text-[11px] uppercase tracking-widest"
                        style={{ background: "rgba(230,57,70,0.95)", color: "#fff", fontWeight: 700 }}
                        data-testid={`sold-badge-${v.id}`}
                      >
                        Sold
                      </div>
                    )}
                    <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded font-heading text-lg" style={{ background: "linear-gradient(135deg,rgba(212,175,55,0.95),rgba(230,57,70,0.85))", color: "#0a0a0a", fontWeight: 800 }}>
                      ${v.price.toLocaleString()}
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-slate-400">{v.year} · {v.color}</div>
                    <h3 className="font-heading text-xl mt-1 mb-2 chrome-text inline-block">{v.make} {v.model}</h3>
                    <div className="flex gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> {v.mileage?.toLocaleString()} mi</span>
                      <span className="flex items-center gap-1"><Fuel className="w-3 h-3" /> {v.fuel_type}</span>
                      <span className="flex items-center gap-1"><Cog className="w-3 h-3" /> {v.transmission}</span>
                    </div>
                  </div>
                </CardTag>
              );
            })}
          </div>
        )}
      </section>
    </PublicShell>
  );
}
